import { IAccountActions } from "@/account/account-actions";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import type { IEditContentPopupProps } from "@/common/paid-content/edit-content-popup";
import type { IUploadContentPopupProps } from "@/common/paid-content/upload-content-popup";
import type { IGetRequest, IPostRequest } from "@/utils/async";
import { ERequestStatus, requestInit } from "@/utils/async";
import { createReducer } from "@reduxjs/toolkit";
import { IFeedActions } from "./feed-actions";
import type { IGetFeedConfig } from "./feed-helpers";
import type { IFeedInfo } from "./feed-models";

export interface IFeedModel {
  feeds: null | IFeedInfo[];
  isSubscribed: boolean;
  getFeed: IGetRequest<any>;
  uploadContentConfig: IUploadContentPopupProps | null;
  editContentConfig: IEditContentPopupProps | null;
  nextFeedUrl: string | null;
  unreadFeeds: number;
  // deleteFeedItemById: IRequest<any>;
  // patchFeedItemById: IRequest<any>;
  // getFeedById: IGetRequest<any>;
  nextFeedUrlById: string | null;
  // deleteFmFeedItemById: IRequest<any>;
  // getFollowing: IGetRequest<any>;
  patchFeedItem: IPostRequest<any, any>;
}

const INITIAL_STATE: IFeedModel = {
  feeds: null,
  isSubscribed: false,
  getFeed: requestInit(),
  uploadContentConfig: null,
  editContentConfig: null,
  nextFeedUrl: null,
  unreadFeeds: 0,
  // deleteFeedItemById: requestInit(),
  // patchFeedItemById: requestInit(),
  // getFeedById: requestInit(),
  nextFeedUrlById: null,
  // deleteFmFeedItemById: requestInit(),
  // getFollowing: requestInit(),
  patchFeedItem: requestInit(),
};

/** Reducer */
export const feedReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(IFeedActions.initGetFeed, (state, action: any): IFeedModel => {
      const { isForceLoader } = action.payload.config as IGetFeedConfig;
      const nextState = {
        getFeed: {
          status: ERequestStatus.Fetching,
        },
        // feeds: isInitialRequest ? null : state.feeds
        feeds: isForceLoader ? null : state.feeds,
      };
      return {
        ...state,
        ...nextState,
      };
    })
    .addCase(IFeedActions.successGetFeed, (state, action: any): IFeedModel => {
      const config: IGetFeedConfig = action.payload.config;
      const res = action.payload.res;

      let feeds: IFeedInfo[] = res.data;
      const getFeedNextUrl: string = res.next;
      const isSubscribed: boolean = res.is_subscribed;

      if (!config.isInitialRequest) {
        feeds = [...(state.feeds || []), ...(res.data || [])];
      } else {
        if (state.feeds && state.feeds.length > 0) {
          // e.g. pull down to refresh case
          // we need to persist client side feeds here

          // get current client side feeds if any
          const clientSideFeeds = state.feeds.filter(
            ({ media }) => media.isClientSide
          );

          // prepend the existing client side feeds to the new feeds
          if (clientSideFeeds.length > 0) {
            feeds = [...clientSideFeeds, ...(res.data || [])];
          }
        }
      }

      return {
        ...state,
        nextFeedUrl: getFeedNextUrl,
        isSubscribed,
        // unreadFeeds: res.unread_feeds,
        unreadFeeds: 0, // reset
        feeds,
        getFeed: {
          responseData: action.payload.res,
          status: ERequestStatus.Fetched,
        },
      };
    })
    .addCase(IFeedActions.failureGetFeed, (state, action: any): IFeedModel => {
      return {
        ...state,
        getFeed: {
          error: action.payload,
          status: ERequestStatus.Error,
        },
      };
    })
    .addCase(
      IFeedActions.toggleUploadContent,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          uploadContentConfig: action.payload.uploadContentConfig,
        };
      }
    )
    .addCase(
      IFeedActions.toggleEditContent,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          editContentConfig: action.payload.editContentConfig,
        };
      }
    )
    .addCase(IFeedActions.initPostFmFeed, (state, action: any) => {
      return {
        ...state,
        feeds: [action.payload.clientSideFeedItem, ...(state.feeds || [])],
        // postFmFeed: {
        //   status: ERequestStatus.Fetching
        // }
      };
    })
    .addCase(
      IFeedActions.progressPostFmFeed,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          feeds: (state.feeds || []).map((feedItem) => {
            if (feedItem._id === action.payload.clientSideFeedItem._id) {
              return action.payload.clientSideFeedItem;
            }
            return feedItem;
          }),
        };
      }
    )
    .addCase(
      IFeedActions.successPostFmFeed,
      (state, action: any): IFeedModel => {
        const { res, clientSideId } = action.payload;
        return {
          ...state,
          feeds: (state.feeds || []).map((feedItem) => {
            if (feedItem._id === clientSideId) {
              return res;
            }
            return feedItem;
          }),
        };
      }
    )
    .addCase(
      IFeedActions.failurePostFmFeed,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          feeds: (state.feeds || []).filter(
            (feedItem) => feedItem._id !== action.payload.clientSideFeedItem._id
          ),
        };
      }
    )
    .addCase(
      IFeedActions.initPatchFeedItem,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          patchFeedItem: {
            status: ERequestStatus.Fetching,
          },
        };
      }
    )
    .addCase(
      IFeedActions.successPatchFeedItem,
      (state, action: any): IFeedModel => {
        const { res } = action.payload;
        return {
          ...state,
          patchFeedItem: {
            responseData: res.data,
            status: ERequestStatus.Fetched,
          },
          feeds: (state.feeds || []).map((feedItem) =>
            feedItem._id === res.data._id ? res.data : feedItem
          ),
        };
      }
    )
    .addCase(
      IFeedActions.failurePatchFeedItem,
      (state, action: any): IFeedModel => {
        const { error } = action.payload;
        return {
          ...state,
          patchFeedItem: {
            error,
            status: ERequestStatus.Error,
          },
        };
      }
    )
    .addCase(
      IFeedActions.resetPatchFeedItem,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          patchFeedItem: requestInit(),
        };
      }
    )
    // [IFeedActions.Type.DELETE__FEED_ITEM_BY_ID__INIT]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFeedItemById: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // },
    // [IFeedActions.Type.DELETE__FEED_ITEM_BY_ID__SUCCESS]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFeedItemById: {
    //       responseData: action.payload,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // },
    // [IFeedActions.Type.DELETE__FEED_ITEM_BY_ID__FAILURE]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFeedItemById: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // },
    .addCase(
      IHeaderModalActions.successDeleteFeedItem,
      (state, action: any): IFeedModel => {
        const { id } = action.payload;
        return {
          ...state,
          feeds: (state.feeds || []).filter(
            (feedItem: IFeedInfo) => feedItem._id !== id
          ),
        };
      }
    )
    // [IFeedActions.Type.PATCH__FEED_ITEM_BY_ID__INIT]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     patchFeedItemById: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // },
    // [IFeedActions.Type.PATCH__FEED_ITEM_BY_ID__SUCCESS]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     patchFeedItemById: {
    //       responseData: action.payload,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // },
    // [IFeedActions.Type.PATCH__FEED_ITEM_BY_ID__FAILURE]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     patchFeedItemById: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FEED_BY_ID__INIT]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFeedById: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FEED_BY_ID__SUCCESS]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFeedById: {
    //       responseData: action.payload,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FEED_BY_ID__FAILURE]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFeedById: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // },
    // [IFeedActions.Type.DELETE__FM_FEED_ITEM_BY_ID__INIT]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFmFeedItemById: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // },
    // [IFeedActions.Type.DELETE__FM_FEED_ITEM_BY_ID__SUCCESS]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFmFeedItemById: {
    //       responseData: action.payload,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // },
    // [IFeedActions.Type.DELETE__FM_FEED_ITEM_BY_ID__FAILURE]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     deleteFmFeedItemById: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FOLLOWING__INIT]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFollowing: {
    //       status: ERequestStatus.Fetching
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FOLLOWING__SUCCESS]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFollowing: {
    //       responseData: action.payload,
    //       status: ERequestStatus.Fetched
    //     }
    //   };
    // },
    // [IFeedActions.Type.GET__FOLLOWING__FAILURE]: (state, action: any): IFeedModel => {
    //   return {
    //     ...state,
    //     getFollowing: {
    //       error: action.payload,
    //       status: ERequestStatus.Error
    //     }
    //   };
    // },
    .addCase(IFeedActions.setUnreadFeeds, (state, action: any): IFeedModel => {
      return {
        ...state,
        unreadFeeds: action.payload,
      };
    })
    .addCase(
      IAccountActions.successGetUser,
      (state, action: any): IFeedModel => {
        return {
          ...state,
          unreadFeeds: action.payload.unread_feeds || 0,
        };
      }
    );
});
